'use client';

import { forwardRef } from 'react';
import classnames from 'classnames';
import Box from '../Box/Box';
import Typography from '../Typography/Typography';
import styles from './FileUpload.module.scss';
import { useUploadState } from './hooks/useUploadState';
import Inline from './Inline/Inline';
import Dropzone from './Dropzone/Dropzone';
import {
  DEFAULT_MAX_RETRIES,
  DEFAULT_RETRY_DELAY_MS,
  DEFAULT_TIMEOUT_MS,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_ACCEPTED_TYPES,
} from './utils';
import type { FileUploadProps } from './types';

// Re-export types for backward compatibility
export type {
  FileUploadProps,
  InitialFile,
  FileValidationError,
  FileValidationErrorType,
} from './types';

/**
 * FileUpload component with drag and drop functionality
 *
 * @component
 * @example
 * ```tsx
 * <FileUpload
 *   dataTestId="case-attachments"
 *   theme="light"
 *   getUploadUrl={async (file) => {
 *     const response = await api.post('/upload-url', { filename: file.name });
 *     return response.data;
 *   }}
 *   onUploadComplete={(url) => console.log('Uploaded:', url)}
 * />
 * ```
 */
const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      dataTestId,
      theme = 'dark',
      variant = 'dropzone',
      accept = DEFAULT_ACCEPTED_TYPES,
      maxFileSize = DEFAULT_MAX_FILE_SIZE,
      multiple = false,
      label = 'Attachments',
      uploadButtonLabel,
      helperText = 'PDF, JPG, PNG, LOG files supported (max 10 MB each)',
      dropzoneText = 'Drag and drop files here, or click to upload',
      getUploadUrl,
      onUploadComplete,
      onUploadError,
      onFileRemove,
      className,
      disabled = false,
      maxRetries = DEFAULT_MAX_RETRIES,
      retryDelayMs = DEFAULT_RETRY_DELAY_MS,
      timeoutMs = DEFAULT_TIMEOUT_MS,
      onRetry,
      required = false,
      validationResponse,
      showValidation = true,
      onValidationError,
      initialFiles = [],
      documentsTitle = 'Documents Available',
      onDocumentClick,
      showDocumentCheckboxes = false,
      removable = false,
    },
    ref
  ) => {
    const {
      files,
      isDragging,
      fileInputRef,
      handleRemoveFile,
      handleRetryFile,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      handleInputChange,
    } = useUploadState({
      getUploadUrl,
      accept,
      maxFileSize,
      multiple,
      maxRetries,
      retryDelayMs,
      timeoutMs,
      disabled,
      initialFiles,
      onUploadComplete,
      onUploadError,
      onFileRemove,
      onRetry,
      onValidationError,
    });

    const getValidationClass = () => {
      if (!validationResponse?.validationType) return undefined;
      const typeMap: Record<string, string> = {
        success: 'validationSuccess',
        warning: 'validationWarning',
        error: 'validationError',
      };
      return styles[typeMap[validationResponse.validationType]];
    };

    return (
      <Box
        ref={ref}
        className={classnames(
          styles.container,
          styles[theme],
          styles[variant],
          getValidationClass(),
          className
        )}
        data-testid={dataTestId}
        data-theme={theme}
        data-variant={variant}
      >
        {variant === 'inline' ? (
          <Inline
            dataTestId={dataTestId}
            theme={theme}
            label={label}
            uploadButtonLabel={uploadButtonLabel}
            helperText={helperText}
            required={required}
            disabled={disabled}
            accept={accept}
            files={files}
            initialFiles={initialFiles}
            fileInputRef={fileInputRef}
            onInputChange={handleInputChange}
            onRemoveFile={handleRemoveFile}
            onRetryFile={handleRetryFile}
            onDocumentClick={onDocumentClick}
            removable={removable}
          />
        ) : (
          <Dropzone
            dataTestId={dataTestId}
            theme={theme}
            label={label}
            helperText={helperText}
            dropzoneText={dropzoneText}
            required={required}
            disabled={disabled}
            accept={accept}
            multiple={multiple}
            isDragging={isDragging}
            files={files}
            initialFiles={initialFiles}
            documentsTitle={documentsTitle}
            showDocumentCheckboxes={showDocumentCheckboxes}
            fileInputRef={fileInputRef}
            onInputChange={handleInputChange}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onRemoveFile={handleRemoveFile}
            onRetryFile={handleRetryFile}
            onDocumentClick={onDocumentClick}
            removable={removable}
          />
        )}

        {showValidation && (
          <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
            {validationResponse?.message && (
              <Typography
                className={styles.validationMessage}
                variant="body"
                size="extraSmall"
                theme={theme}
                data-testid={`${dataTestId}-validation-message`}
              >
                {validationResponse.message}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

FileUpload.displayName = 'FileUpload';

export default FileUpload;
